import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Divider, Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addDestination, editDestination } from '../../../../api/endpoints/cmsContent';
import type { Destination } from '../../../../api/types/cmsContent';
import { FormDialog } from '../../../../components/common';
import { DestinationDetailsSection } from './components/DestinationDetailsSection';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  language: z.string().optional(),
  summary: z.string().optional(),
  link: z.string().optional(),
  imgPath: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  title: '',
  language: 'TR',
  summary: '',
  link: '',
  imgPath: '',
};

function toFormValues(destination: Destination): FormValues {
  return {
    title: destination.title ?? '',
    language: destination.language ?? 'TR',
    summary: destination.summary ?? '',
    link: destination.link ?? '',
    imgPath: destination.imgPath ?? '',
  };
}

/** Create/edit dialog for Destinasyonlar (Destination). Confirmed via local-backend swagger +
 *  full live curl round-trip (see api/types/cmsContent.ts header). There is no
 *  `GetDestinationById` route (confirmed) — `destination` is the row straight from the list
 *  page's query cache, including its already-fetched `destinationDetails`. In edit mode, also
 *  renders `DestinationDetailsSection` — the ONLY UI for that child resource, since it has no
 *  standalone list route (see that component's doc comment and api/types/cmsContent.ts).
 *  Always rendered inside a specific site's tab, so `site_Id` is injected from the `siteId`
 *  prop. */
export function DestinationFormDialog({
  open,
  destination,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  destination: Destination | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!destination;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(destination ? toFormValues(destination) : emptyValues);
  }, [open, destination, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && destination
        ? editDestination({ id: destination.id, site_Id: siteId, ...values })
        : addDestination({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Destinasyon güncellendi.' : 'Destinasyon oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'destinations'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Destinasyonu Güncelle' : 'Yeni Destinasyon'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Dil" {...register('language')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Link" {...register('link')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Görsel Yolu" {...register('imgPath')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={3} label="Özet" {...register('summary')} />
            </Grid>
          </Grid>

          {isEdit && destination && (
            <>
              <Divider />
              <DestinationDetailsSection
                destinationId={destination.id}
                initialDetails={destination.destinationDetails ?? []}
              />
            </>
          )}

    </FormDialog>
  );
}
